import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Send, Brain, Sparkles, Loader2, Save, Globe, Bot, Image as ImageIcon, Film } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import FactsDrawer from "@/components/FactsDrawer";
import WebSourcesDrawer from "@/components/WebSourcesDrawer";
import ReviewDialog from "@/components/ReviewDialog";
import { metaFor, ACCENT } from "@/lib/models";
import api, { API } from "@/lib/api";

export default function ChatPage() {
  const { conversaId } = useParams();
  const navigate = useNavigate();
  const { reloadConversas, conversas } = useOutletContext();

  const [models, setModels] = useState([]);
  const [model, setModel] = useState(localStorage.getItem("sm_model") || "claude-opus-5-5");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [pendingFacts, setPendingFacts] = useState(null);
  const [drawerFacts, setDrawerFacts] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [webDrawerOpen, setWebDrawerOpen] = useState(false);
  const [drawerWeb, setDrawerWeb] = useState(null);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [web, setWeb] = useState(localStorage.getItem("sm_web") === "1");
  const [webAvailable, setWebAvailable] = useState(true);
  const [agentesMap, setAgentesMap] = useState({});

  useEffect(() => {
    api.get("/agentes").then((r) => {
      const map = {};
      r.data.forEach((a) => (map[a.id] = a));
      setAgentesMap(map);
    }).catch(() => {});
  }, []);

  const convAtual = (conversas || []).find((c) => c.id === conversaId);
  const agente = convAtual?.agente_id ? agentesMap[convAtual.agente_id] : null;
  const modelosDisponiveis = agente
    ? models.filter((m) => agente.modelos.includes(m.id))
    : models;

  useEffect(() => {
    if (agente) {
      setModel(agente.modelo_default);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agente?.id]);

  const feedRef = useRef(null);
  const streamTextRef = useRef("");
  const selfNavRef = useRef(null); // conversaId que criámos nós — não recarregar por cima do stream
  const promptedRef = useRef(new Set()); // conversas onde já sugerimos guardar

  useEffect(() => {
    api.get("/models").then((r) => {
      setModels(r.data);
      // se o modelo guardado já não existir (ids antigos ou sem chave), usa o primeiro disponível
      setModel((m) => (r.data.some((x) => x.id === m) || !r.data.length ? m : r.data[0].id));
    }).catch(() => {});
    api.get("/fontes/disponivel").then((r) => setWebAvailable(r.data.web)).catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem("sm_model", model);
  }, [model]);

  const loadMessages = useCallback(async () => {
    if (!conversaId) {
      setMessages([]);
      return;
    }
    if (selfNavRef.current === conversaId) {
      // acabámos de navegar para esta conversa a meio de um envio — não apagar o stream
      selfNavRef.current = null;
      return;
    }
    try {
      const { data } = await api.get(`/conversas/${conversaId}/mensagens`);
      setMessages(data);
    } catch (e) {
      setMessages([]);
    }
  }, [conversaId]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
    });
  };

  useEffect(scrollToBottom, [messages, streaming]);

  const ensureConv = async () => {
    if (conversaId) return conversaId;
    const { data } = await api.post("/conversas", { titulo: input.trim().slice(0, 50) || "Nova conversa" });
    selfNavRef.current = data.id;
    await reloadConversas();
    navigate(`/c/${data.id}`, { replace: true });
    return data.id;
  };

  const [imgMode, setImgMode] = useState(false);
  const [imgCount, setImgCount] = useState(1);
  const [busyMedia, setBusyMedia] = useState(false);

  const gerarImagem = async () => {
    const text = input.trim();
    if (!text || busyMedia) return;
    const convId = await ensureConv();
    setInput("");
    setBusyMedia(true);
    try {
      const modelo = agente?.modelo_imagem || "gpt-image-1";
      await api.post("/chat/imagem", { conversa_id: convId, prompt: text, n: imgCount, model: modelo });
      const { data } = await api.get(`/conversas/${convId}/mensagens`);
      setMessages(data);
    } catch (e) {
      toast.error("Falha ao gerar imagem (verifica a OPENAI_API_KEY e os créditos)");
    } finally {
      setBusyMedia(false);
    }
  };

  const gerarVideo = async () => {
    const text = input.trim();
    if (!text || busyMedia) return;
    const convId = await ensureConv();
    setInput("");
    setBusyMedia(true);
    toast("A gerar vídeo com Sora — pode demorar 2-5 min…", { duration: 6000 });
    try {
      const { data } = await api.post("/chat/video", { conversa_id: convId, prompt: text });
      const jobId = data.job_id;
      const poll = setInterval(async () => {
        try {
          const st = await api.get(`/chat/video/${jobId}`);
          if (st.data.estado === "concluido" || st.data.estado === "erro") {
            clearInterval(poll);
            setBusyMedia(false);
            if (st.data.estado === "erro") toast.error("Falha no vídeo: " + (st.data.erro || ""));
            else toast.success("Vídeo pronto!");
            const { data: msgs } = await api.get(`/conversas/${convId}/mensagens`);
            setMessages(msgs);
          }
        } catch (_) {}
      }, 6000);
    } catch (e) {
      setBusyMedia(false);
      toast.error("Não foi possível iniciar o vídeo");
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;

    let convId = conversaId;
    if (!convId) {
      const { data } = await api.post("/conversas", { titulo: text.slice(0, 50) });
      convId = data.id;
      selfNavRef.current = convId;
      await reloadConversas();
      navigate(`/c/${convId}`, { replace: true });
    }

    setInput("");
    setMessages((m) => [...m, { papel: "user", texto: text, id: `tmp-${Date.now()}` }]);
    setStreaming(true);
    setPendingFacts(null);
    streamTextRef.current = "";
    setMessages((m) => [
      ...m,
      { papel: "assistant", texto: "", model, factos_usados: [], id: "streaming", streaming: true },
    ]);

    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("sm_token")}`,
        },
        body: JSON.stringify({ conversa_id: convId, mensagem: text, model, web }),
      });
      if (!resp.ok || !resp.body) throw new Error("Falha na ligação");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.type === "facts") {
            setPendingFacts(payload.factos);
            setMessages((m) =>
              m.map((msg) =>
                msg.id === "streaming" ? { ...msg, factos_usados: payload.factos } : msg
              )
            );
          } else if (payload.type === "web") {
            setMessages((m) =>
              m.map((msg) =>
                msg.id === "streaming" ? { ...msg, web_sources: payload.sources } : msg
              )
            );
          } else if (payload.type === "delta") {
            streamTextRef.current += payload.content;
            setMessages((m) =>
              m.map((msg) =>
                msg.id === "streaming" ? { ...msg, texto: streamTextRef.current } : msg
              )
            );
          } else if (payload.type === "error") {
            toast.error(payload.message);
            setMessages((m) =>
              m.map((msg) =>
                msg.id === "streaming"
                  ? {
                      ...msg,
                      texto: (msg.texto ? msg.texto + "\n\n" : "") + `⚠️ ${payload.message}`,
                      error: true,
                    }
                  : msg
              )
            );
          } else if (payload.type === "done") {
            if (payload.should_review && !promptedRef.current.has(convId)) {
              promptedRef.current.add(convId);
              toast("Esta conversa já dá para guardar factos na memória.", {
                action: { label: "Rever e guardar", onClick: () => guardarConversa(convId) },
                duration: 8000,
              });
            }
          }
        }
      }
    } catch (e) {
      toast.error("Erro no streaming: " + e.message);
    } finally {
      setStreaming(false);
      // usa convId concreto (loadMessages podia estar preso ao conversaId antigo=undefined)
      try {
        const { data } = await api.get(`/conversas/${convId}/mensagens`);
        setMessages(data);
      } catch (_) {
        /* mantém as mensagens otimistas */
      }
      await reloadConversas();
    }
  };

  const guardarConversa = async (cid) => {
    const target = cid || conversaId;
    if (!target) return;
    setDraft(null);
    setExtracting(true);
    setReviewOpen(true);
    try {
      const { data } = await api.post(`/conversas/${target}/extrair`);
      setDraft(data);
    } catch (e) {
      toast.error("Não foi possível extrair factos");
      setReviewOpen(false);
    } finally {
      setExtracting(false);
    }
  };

  const guardarMemoria = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.post("/memorias/guardar", {
        conversa_id: conversaId,
        fonte: "chat",
        ...payload,
      });
      const extra = data.substituidos ? ` (${data.substituidos} substituído(s))` : "";
      toast.success(`${data.inseridos} facto(s) guardado(s) na memória${extra}`);
      setReviewOpen(false);
    } catch (e) {
      toast.error("Não foi possível guardar");
    } finally {
      setSaving(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const openFacts = (factos) => {
    setDrawerFacts(factos);
    setDrawerOpen(true);
  };

  const openWeb = (sources) => {
    setDrawerWeb(sources);
    setWebDrawerOpen(true);
  };

  const meta = metaFor(model);
  const accent = ACCENT[meta.accent];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800/60 px-4 lg:px-6 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 pl-10 lg:pl-0">
          {agente && (
            <span data-testid="chat-agente-badge" className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-semibold">
              <Bot size={13} /> {agente.nome} · Nível {agente.nivel}
            </span>
          )}
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger
              data-testid="chat-model-selector"
              className="w-[190px] bg-slate-900/80 border-slate-700/60 text-sm h-9 focus:ring-emerald-500/30"
            >
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${accent.dot}`} />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              {(modelosDisponiveis.length ? modelosDisponiveis : [{ id: model, label: meta.label }]).map((m) => {
                const mm = metaFor(m.id);
                return (
                  <SelectItem key={m.id} value={m.id} data-testid={`model-option-${m.id}`}>
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${ACCENT[mm.accent].dot}`} />
                      {m.label}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          {conversaId && messages.some((m) => m.imagens?.length) && (
            <button
              data-testid="catalogo-pdf-button"
              onClick={async () => {
                try {
                  const res = await api.get(`/conversas/${conversaId}/catalogo`, { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url; a.download = `catalogo-${conversaId.slice(0, 6)}.pdf`; a.click();
                  URL.revokeObjectURL(url);
                } catch (e) { toast.error("Não foi possível exportar o catálogo"); }
              }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-fuchsia-500/15 border border-fuchsia-500/40 text-fuchsia-300 text-xs font-semibold transition-colors hover:bg-fuchsia-500/25"
            >
              <ImageIcon size={13} /> Catálogo PDF
            </button>
          )}
          {conversaId && messages.length > 0 && (
            <button
              data-testid="save-conversation-button"
              onClick={() => guardarConversa(conversaId)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800/70 hover:bg-slate-700/70 border border-slate-700 text-slate-200 text-xs font-semibold transition-colors"
            >
              <Save size={13} /> Guardar conversa
            </button>
          )}
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-xs font-mono">
            <Brain size={13} /> Memória partilhada ativa
          </div>
        </div>
      </header>

      {/* Feed */}
      <div ref={feedRef} className="flex-1 overflow-y-auto px-4 lg:px-6 py-6 scroll-smooth">
        {messages.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="max-w-3xl mx-auto w-full space-y-6">
            {messages.map((m, i) => (
              <MessageBubble key={m.id || i} m={m} onOpenFacts={openFacts} onOpenWeb={openWeb} />
            ))}
          </div>
        )}
      </div>

      {/* Input */}
      <div className="sticky bottom-0 px-4 lg:px-6 pb-5 pt-2 bg-gradient-to-t from-[#080c14] via-[#080c14]/95 to-transparent">
        <div className="max-w-3xl mx-auto w-full bg-slate-900/90 backdrop-blur-lg border border-slate-700/60 focus-within:border-emerald-500/60 focus-within:ring-2 focus-within:ring-emerald-500/15 rounded-2xl p-2.5 shadow-2xl transition-all flex items-end gap-2">
          <textarea
            data-testid="chat-message-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            rows={1}
            placeholder="Escreve uma mensagem…"
            className="flex-1 bg-transparent resize-none max-h-40 px-3 py-2 text-sm placeholder:text-slate-500 focus:outline-none"
            style={{ minHeight: "40px" }}
          />
          {webAvailable && (
            <button
              data-testid="chat-web-toggle"
              onClick={() => { const nv = !web; setWeb(nv); localStorage.setItem("sm_web", nv ? "1" : "0"); }}
              title="Pesquisar na web (Tavily)"
              className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-colors border ${web ? "bg-sky-500/20 border-sky-500/50 text-sky-300" : "bg-slate-800/60 border-slate-700 text-slate-400 hover:text-slate-200"}`}
            >
              <Globe size={17} />
            </button>
          )}
          <button
            data-testid="chat-image-toggle"
            onClick={() => setImgMode((v) => !v)}
            title="Gerar imagem"
            className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-colors border ${imgMode ? "bg-fuchsia-500/20 border-fuchsia-500/50 text-fuchsia-300" : "bg-slate-800/60 border-slate-700 text-slate-400 hover:text-slate-200"}`}
          >
            <ImageIcon size={17} />
          </button>
          {imgMode && (
            <select
              data-testid="chat-image-count"
              value={imgCount}
              onChange={(e) => setImgCount(Number(e.target.value))}
              className="shrink-0 h-10 rounded-xl bg-slate-800/60 border border-slate-700 text-slate-200 text-sm px-2"
              title="Nº de imagens (catálogo)"
            >
              {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}🖼</option>)}
            </select>
          )}
          <button
            data-testid="chat-video-button"
            onClick={gerarVideo}
            disabled={busyMedia || !input.trim()}
            title="Gerar vídeo (Sora)"
            className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-colors border bg-slate-800/60 border-slate-700 text-slate-400 hover:text-slate-200 disabled:opacity-40"
          >
            <Film size={17} />
          </button>
          <button
            data-testid="chat-send-button"
            onClick={imgMode ? gerarImagem : send}
            disabled={streaming || busyMedia || !input.trim()}
            className="shrink-0 w-10 h-10 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 flex items-center justify-center transition-colors disabled:opacity-40"
          >
            {streaming || busyMedia ? <Loader2 size={17} className="animate-spin" /> : (imgMode ? <ImageIcon size={17} /> : <Send size={17} />)}
          </button>
        </div>
        <p className="text-center text-[11px] text-slate-600 mt-2 font-mono">
          {busyMedia ? "a gerar media…" : imgMode ? `modo imagem · ${imgCount} imagem(ns) · ${agente?.modelo_imagem || "gpt-image-1"}` : web ? "pesquisa web ligada · nada é guardado automaticamente" : "🎨 imagem · 🎬 vídeo (Sora) · 🌐 web"}
        </p>
      </div>

      <FactsDrawer open={drawerOpen} onOpenChange={setDrawerOpen} factos={drawerFacts} />
      <WebSourcesDrawer open={webDrawerOpen} onOpenChange={setWebDrawerOpen} sources={drawerWeb} />
      <ReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        extracting={extracting}
        saving={saving}
        draft={draft}
        onSave={guardarMemoria}
      />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center max-w-md mx-auto sm-fade-up">
      <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center mb-5">
        <Brain size={30} className="text-emerald-400" />
      </div>
      <h2 className="font-display text-2xl font-bold mb-2">Uma memória, todos os modelos</h2>
      <p className="text-sm text-slate-400 leading-relaxed">
        O que ensinas ao GPT fica disponível quando falas com o Claude. Começa a escrever e a
        memória enche-se sozinha com os factos que importam.
      </p>
    </div>
  );
}

function MessageBubble({ m, onOpenFacts, onOpenWeb }) {
  const isUser = m.papel === "user";
  const meta = metaFor(m.model);
  const accent = ACCENT[meta.accent] || ACCENT.emerald;

  if (isUser) {
    return (
      <div className="flex justify-end sm-fade-up">
        <div className="bg-slate-800/70 border border-slate-700/50 text-slate-100 rounded-2xl rounded-br-md px-4 py-3 max-w-[85%] text-sm leading-relaxed whitespace-pre-wrap">
          {m.texto}
        </div>
      </div>
    );
  }

  const factCount = m.factos_usados?.length || 0;
  return (
    <div className="flex flex-col gap-2 sm-fade-up" data-testid="assistant-message">
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${accent.bg} ${accent.text} border ${accent.border}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${accent.dot}`} /> {meta.label}
        </span>
      </div>
      <div className="bg-slate-900/90 border border-slate-800/80 text-slate-100 rounded-2xl rounded-tl-md px-5 py-4 shadow-lg">
        <p
          className={`text-sm leading-relaxed whitespace-pre-wrap ${
            m.streaming && !m.texto ? "sm-caret text-slate-500" : ""
          } ${m.error ? "text-amber-300" : ""}`}
        >
          {m.texto || (m.streaming ? "a pensar" : "")}
        </p>
        {m.imagens?.length > 0 && (
          <div data-testid="message-imagens" className={`mt-3 grid gap-2 ${m.imagens.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
            {m.imagens.map((b64, i) => (
              <a key={i} href={`data:image/png;base64,${b64}`} target="_blank" rel="noreferrer">
                <img src={`data:image/png;base64,${b64}`} alt={`imagem ${i + 1}`} className="rounded-xl border border-slate-700 w-full object-cover" />
              </a>
            ))}
          </div>
        )}
        {m.video && (
          <video data-testid="message-video" controls className="mt-3 rounded-xl border border-slate-700 w-full max-w-md">
            <source src={`data:video/mp4;base64,${m.video}`} type="video/mp4" />
          </video>
        )}
      </div>
      {!m.streaming && (
        <div className="flex flex-wrap items-center gap-2">
        <button
          data-testid="facts-used-badge"
          onClick={() => onOpenFacts(m.factos_usados || [])}
          className="self-start inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-mono hover:bg-emerald-500/20 cursor-pointer transition-colors"
        >
          <Sparkles size={12} /> {factCount} {factCount === 1 ? "facto utilizado" : "factos utilizados"}
        </button>
        {m.web_sources?.length > 0 && (
          <button
            data-testid="web-sources-badge"
            onClick={() => onOpenWeb(m.web_sources)}
            className="self-start inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-300 text-xs font-mono hover:bg-sky-500/20 cursor-pointer transition-colors"
          >
            <Globe size={12} /> {m.web_sources.length} {m.web_sources.length === 1 ? "fonte web" : "fontes web"}
          </button>
        )}
        </div>
      )}
    </div>
  );
}
